import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';

import type { NormalizedModule, SelectionKey } from '../../types/merge-customization';

import { ModuleItem } from './ModuleItem';

interface ModuleSectionProps {
  module: NormalizedModule;
  moduleName: string;
  selections: Set<SelectionKey>;
  onToggleItem: (key: SelectionKey) => void;
  onSelectAllFromPatient: (moduleName: string, patientId: string) => void;
  isDuplicate: (key: SelectionKey) => boolean;
  patientColors: Map<string, string>;
  /** Unique patients that have items in this module */
  patients: { id: string; name: string }[];
  expanded?: boolean;
  onToggle?: () => void;
}

export function ModuleSection({
  module,
  moduleName,
  selections,
  onToggleItem,
  onSelectAllFromPatient,
  isDuplicate,
  patientColors,
  patients,
  expanded,
  onToggle,
}: ModuleSectionProps) {
  const selectedCount = module.items.filter(i => selections.has(i.selectionKey)).length;
  const totalCount = module.items.length;

  return (
    <Accordion expanded={expanded} onChange={onToggle} disableGutters sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 48,
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {module.name}
        </Typography>
        <Chip
          label={`${selectedCount} / ${totalCount}`}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.75rem' }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {/* Select-all shortcuts */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, alignSelf: 'center' }}>
            Select all from:
          </Typography>
          {patients.map(p => (
            <Button
              key={p.id}
              size="small"
              variant="outlined"
              onClick={() => onSelectAllFromPatient(moduleName, p.id)}
              sx={{
                textTransform: 'none',
                fontSize: '0.7rem',
                py: 0.25,
                px: 1,
                minWidth: 0,
                borderColor: patientColors.get(p.id) ?? 'grey.400',
                color: patientColors.get(p.id) ?? 'text.primary',
                '&:hover': {
                  borderColor: patientColors.get(p.id) ?? 'grey.600',
                  backgroundColor: `${patientColors.get(p.id) ?? '#888'}10`,
                },
              }}
            >
              {p.name}
            </Button>
          ))}
        </Box>

        {/* Items */}
        <Box sx={{ px: 0.5, py: 0.5 }}>
          {module.items.map(item => (
            <ModuleItem
              key={item.selectionKey}
              item={item}
              isSelected={selections.has(item.selectionKey)}
              isDuplicate={isDuplicate(item.selectionKey)}
              onToggle={onToggleItem}
              patientColor={patientColors.get(item.patientId) ?? '#888'}
            />
          ))}
          {module.items.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
              No items in this module.
            </Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
